import type { ComputedRef, MaybeRefOrGetter, WatchStopHandle } from 'vue';
import type { BlockRootContext, BlockRootContextPropertyResult } from '#layers/block-engine/types/block';
import { computed } from 'vue';
import { useBlockRootContextStore } from '#layers/block-engine/stores/block-root-context';
import { exposeToBlockRootContext as exposeBlockRootContextProperty } from './context';

export interface ConsumeBlockRootContextOptions {
	/**
	 * Narrows the registered root contexts that should be searched.
	 */
	filter?: (rootContext: BlockRootContext) => boolean;
}

export interface BlockRootContextPropertyFactory<TValue> {
	/**
	 * Publishes a typed value for this root context property from the current block render.
	 */
	expose: (value: MaybeRefOrGetter<TValue | undefined>) => WatchStopHandle | undefined;
	/**
	 * Reads typed values for this root context property from registered root renders.
	 */
	consume: (options?: ConsumeBlockRootContextOptions) => ComputedRef<Array<BlockRootContextPropertyResult<TValue>>>;
}

/**
 * Creates typed publisher and consumer helpers for one root context property key.
 */
export function createBlockRootContextProperty<TValue>(key: string): BlockRootContextPropertyFactory<TValue> {
	return {
		expose(value) {
			return exposeBlockRootContextProperty<TValue>(key, value);
		},
		consume(options) {
			const store = useBlockRootContextStore();

			return computed(() => store.getContextProperty<TValue>(key, options));
		},
	};
}
